import { z } from 'zod';

// Re-export DBUIBlock for external use
export type { DBUIBlock } from './utils/conversation-saver';

// User schema for authentication and management
export const UserSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  email: z.string().email('Invalid email format').optional(),
  password: z.string().min(1, 'Password is required'),
  fullname: z.string().optional(),
  role: z.string().optional(),
  userInfo: z.record(z.unknown()).optional(),
  wsIds: z.array(z.string()).optional(), // Only in memory, not persisted to file
});

export type User = z.infer<typeof UserSchema>;

export const UsersDataSchema = z.object({
  users: z.array(UserSchema),
});

export type UsersData = z.infer<typeof UsersDataSchema>;

// From/To object schema for WebSocket messages
export const MessageParticipantSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
});

export type MessageParticipant = z.infer<typeof MessageParticipantSchema>;

// Message schemas for WebSocket communication
export const MessageSchema = z.object({
  id: z.string().optional(),
  type: z.string(),
  from: MessageParticipantSchema,
  to: MessageParticipantSchema.optional(),
  payload: z.unknown(),
});

export type Message = z.infer<typeof MessageSchema>;

export const IncomingMessageSchema = z.object({
  id: z.string().optional(),
  type: z.string(),
  from: MessageParticipantSchema,
  to: MessageParticipantSchema.optional(),
  payload: z.unknown(),
});

export type IncomingMessage = z.infer<typeof IncomingMessageSchema>;

// Data request/response schemas
export const DataRequestPayloadSchema = z.object({
  collection: z.string(),
  op: z.string(),
  params: z.record(z.unknown()).optional(),
  SA_RUNTIME: z.record(z.unknown()).optional(),
});

export type DataRequestPayload = z.infer<typeof DataRequestPayloadSchema>;

export const DataRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('DATA_REQ'),
  payload: DataRequestPayloadSchema,
});

export type DataRequestMessage = z.infer<typeof DataRequestMessageSchema>;

export const AuthLoginRequestPayloadSchema = z.object({
  login_data: z.string(),
});

export type AuthLoginRequestPayload = z.infer<typeof AuthLoginRequestPayloadSchema>;

export const AuthLoginRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('AUTH_LOGIN_REQ'),
  payload: AuthLoginRequestPayloadSchema,
});

export type AuthLoginRequestMessage = z.infer<typeof AuthLoginRequestMessageSchema>;

export const AuthVerifyRequestPayloadSchema = z.object({
  token: z.string(),
});

export type AuthVerifyRequestPayload = z.infer<typeof AuthVerifyRequestPayloadSchema>;

export const AuthVerifyRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('AUTH_VERIFY_REQ'),
  payload: AuthVerifyRequestPayloadSchema,
});

export type AuthVerifyRequestMessage = z.infer<typeof AuthVerifyRequestMessageSchema>;

export const UserPromptRequestPayloadSchema = z.object({
  prompt: z.string(),
  userId: z.string().optional(),
  SA_RUNTIME: z.object({
      threadId: z.string(),
      uiBlockId: z.string(),
    }).optional(),
  responseMode: z.enum(['component', 'text']).optional(),
});

export type UserPromptRequestPayload = z.infer<typeof UserPromptRequestPayloadSchema>;

export const UserPromptRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('USER_PROMPT_REQ'),
  payload: UserPromptRequestPayloadSchema,
});

export type UserPromptRequestMessage = z.infer<typeof UserPromptRequestMessageSchema>;

// Abort an in-flight user-prompt request (frontend "Stop" button).
export const UserPromptAbortRequestPayloadSchema = z.object({
  uiBlockId: z.string(),
  threadId: z.string().optional(),
});

export type UserPromptAbortRequestPayload = z.infer<typeof UserPromptAbortRequestPayloadSchema>;

export const UserPromptAbortRequestMessageSchema = z.object({
  id: z.string().optional(),
  from: MessageParticipantSchema,
  type: z.literal('USER_PROMPT_ABORT_REQ'),
  payload: UserPromptAbortRequestPayloadSchema,
});

export type UserPromptAbortRequestMessage = z.infer<typeof UserPromptAbortRequestMessageSchema>;

export const UserPromptSuggestionsPayloadSchema = z.object({
  prompt: z.string(),
  userId: z.string().optional(),
  limit: z.number().int().positive().default(5),
  similarityThreshold: z.number().min(0).max(1).default(0.4),
});

export type UserPromptSuggestionsPayload = z.infer<typeof UserPromptSuggestionsPayloadSchema>;

export const UserPromptSuggestionsMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('USER_PROMPT_SUGGESTIONS_REQ'),
  payload: UserPromptSuggestionsPayloadSchema,
});

export type UserPromptSuggestionsMessage = z.infer<typeof UserPromptSuggestionsMessageSchema>;


export const ComponentPropsSchema = z.object({
  query:  z.string().or(z.object({})).nullable().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  config: z.record(z.unknown()).optional(),
  actions: z.array(z.any()).optional(),
}).passthrough(); // Allow additional props beyond the defined ones

export const ComponentSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().optional(),
  isDisplayComp: z.boolean().optional(),
  type: z.string(),
  description: z.string(),
  props: ComponentPropsSchema,
  category: z.string().optional(),
  keywords: z.array(z.string()).optional(),
});

export const ComponentsSchema = z.array(ComponentSchema);

export type Component = z.infer<typeof ComponentSchema>;

export const ComponentListResponsePayloadSchema = z.object({
  components: z.array(ComponentSchema),
});

export type ComponentListResponsePayload = z.infer<typeof ComponentListResponsePayloadSchema>; 

export const ComponentListResponseMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('COMPONENT_LIST_RES'),
  payload: ComponentListResponsePayloadSchema,
});

export type ComponentListResponseMessage = z.infer<typeof ComponentListResponseMessageSchema>;

// ==================== Workflow Descriptors ====================
// Workflow components are pre-built multi-step UI flows the main agent can
// pick when the user wants to *do* something end-to-end (initiate a transfer,
// create an order, etc.). The descriptor declares routing metadata; the React
// component itself lives in the frontend bundle.

export const WorkflowDescriptorSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  whenToUse: z.string(),
  propsSchema: z.record(z.string()),
  defaultProps: z.record(z.unknown()).optional(),
});

export const WorkflowsSchema = z.array(WorkflowDescriptorSchema);

export type WorkflowDescriptorPayload = z.infer<typeof WorkflowDescriptorSchema>;

export const WorkflowListResponsePayloadSchema = z.object({
  workflows: z.array(WorkflowDescriptorSchema),
});

export type WorkflowListResponsePayload = z.infer<typeof WorkflowListResponsePayloadSchema>;

export const WorkflowListResponseMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('WORKFLOW_LIST_RES'),
  payload: WorkflowListResponsePayloadSchema,
});

export type WorkflowListResponseMessage = z.infer<typeof WorkflowListResponseMessageSchema>;

// Tool output field schema - describes a single field/column in the tool's output
export const OutputFieldSchema = z.object({
  name: z.string(),                    // Field name (column name in the result)
  type: z.enum(['string', 'number', 'boolean', 'date']),
  description: z.string(),             // What this field contains
});

export type OutputField = z.infer<typeof OutputFieldSchema>;

// Tool output schema - describes the array of records returned by a tool
// Tools ALWAYS return an array of records (like SQL query results)
// This helps the LLM understand what data is available for component generation
export const OutputSchema = z.object({
  description: z.string(),             // Brief description of what the output represents
  fields: z.array(OutputFieldSchema),  // List of fields in each record (like columns in a table)
});

export type ToolOutputSchema = z.infer<typeof OutputSchema>;

// Tool schemas
export const ToolSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  /** Tool type: "source" = routed through SourceAgent, "direct" = called directly by MainAgent */
  toolType: z.enum(['source', 'direct']).optional(),
  /** Full untruncated schema for source agent (all columns visible) */
  fullSchema: z.string().optional(),
  params: z.record(z.string()),
  fn: z.function().args(z.any()).returns(z.any()),
  outputSchema: OutputSchema.optional(),  // Optional: describes the data structure returned by this tool
  /** Cache policy. `false` = never cache (live data, write ops). Mirrors HTTP `Cache-Control: no-store`. */
  cache: z.union([z.literal(false), z.object({ ttlMs: z.number().optional() })]).optional(),
});

export type Tool = z.infer<typeof ToolSchema>;

// Admin User Management Request/Response schemas (Unified)

// Query filters schema for users
export const UserQueryFiltersSchema = z.object({
  username: z.string().optional(),
  email: z.string().optional(),
  role: z.string().optional(),
  fullname: z.string().optional(),
  id: z.number().optional()
});

export type UserQueryFilters = z.infer<typeof UserQueryFiltersSchema>;

export const UsersRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    username: z.string().optional(),
    email: z.string().email('Invalid email format').optional(),
    password: z.string().optional(),
    fullname: z.string().optional(),
    role: z.string().optional(),
    userInfo: z.record(z.unknown()).optional(),
    // Query operation fields
    filters: UserQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
  }).optional(),
});

export type UsersRequestPayload = z.infer<typeof UsersRequestPayloadSchema>;

export const UsersRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('USERS'),
  payload: UsersRequestPayloadSchema,
});

export type UsersRequestMessage = z.infer<typeof UsersRequestMessageSchema>;

// UI Logs schema for tracking user request execution
export const UILogsPayloadSchema = z.object({
  logs: z.array(z.object({
    timestamp: z.number(),
    level: z.enum(['info', 'error', 'warn', 'debug']),
    message: z.string(),
    type: z.enum(['explanation', 'query', 'general']).optional(),
    data: z.record(z.unknown()).optional(),
  })),
});

export type UILogsPayload = z.infer<typeof UILogsPayloadSchema>;

export const UILogsMessageSchema = z.object({
  id: z.string(), // uiBlockId
  from: MessageParticipantSchema,
  type: z.literal('UI_LOGS'),
  payload: UILogsPayloadSchema,
});

export type UILogsMessage = z.infer<typeof UILogsMessageSchema>;

// Actions request schema - for runtime to send component actions and request next questions
export const ActionsRequestPayloadSchema = z.object({
  userId: z.string().optional(),
  SA_RUNTIME: z.object({
    threadId: z.string(),
    uiBlockId: z.string(),
  }).optional(),
});

export type ActionsRequestPayload = z.infer<typeof ActionsRequestPayloadSchema>;

export const ActionsRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('ACTIONS'),
  payload: ActionsRequestPayloadSchema,
});

export type ActionsRequestMessage = z.infer<typeof ActionsRequestMessageSchema>;

// Collection operation types
export type CollectionOperation =
  | 'getMany'
  | 'getOne'
  | 'query'
  | 'mutation'
  | 'updateOne'
  | 'deleteOne'
  | 'createOne';

export type CollectionHandler<TParams = any, TResult = any> = (
  params: TParams
) => Promise<TResult> | TResult;

export interface CollectionRegistry {
  [collectionName: string]: {
    [operation: string]: CollectionHandler;
  };
}

export type LLMProvider = 'anthropic' | 'groq' | 'gemini' | 'openai';

import type { LogLevel } from './utils/logger';

export type DatabaseType = 'postgresql' | 'mssql' | 'snowflake' | 'mysql';

/**
 * Model strategy for controlling which models are used for different tasks
 * - 'best': Use the best model (e.g., Sonnet) for all tasks - highest quality, higher cost
 * - 'fast': Use the fast model (e.g., Haiku) for all tasks - lower quality, lower cost
 * - 'balanced': Use best model for complex tasks, fast model for simple tasks (default)
 */
export type ModelStrategy = 'best' | 'fast' | 'balanced';

/**
 * Model configuration for DASH_COMP flow (dashboard component picking)
 * Allows separate control of models used for component selection
 */
export interface DashCompModelConfig {
  /**
   * Primary model for DASH_COMP requests
   * Format: "provider/model-name" (e.g., "anthropic/claude-sonnet-4-5-20250929")
   */
  model?: string;
  /**
   * Fast model for simpler DASH_COMP tasks (optional)
   * Format: "provider/model-name" (e.g., "anthropic/claude-haiku-4-5-20251001")
   */
  fastModel?: string;
}

export interface SuperatomSDKConfig {
  url?: string;
  apiKey?: string;
  projectId: string;
  type?: string;
  bundleDir?: string;
  promptsDir?: string; // Path to custom prompts directory (defaults to .prompts in SDK)
  databaseType?: DatabaseType; // Database type for SQL generation rules (defaults to 'postgresql')
  ANTHROPIC_API_KEY?: string;
  GROQ_API_KEY?: string;
  GEMINI_API_KEY?: string;
  OPENAI_API_KEY?: string;
  LLM_PROVIDERS?: LLMProvider[];
  logLevel?: LogLevel; // Log level for SDK logging (errors, warnings, info, verbose)
  /**
   * Model selection strategy for LLM API calls:
   * - 'best': Use best model for all tasks (highest quality, higher cost)
   * - 'fast': Use fast model for all tasks (lower quality, lower cost)
   * - 'balanced': Use best model for complex tasks, fast model for simple tasks (default)
   */
  modelStrategy?: ModelStrategy;
  /**
   * Model for the main agent (routing + analysis).
   * Format: "provider/model-name" (e.g., "anthropic/claude-haiku-4-5-20251001")
   * If not set, uses the provider's default model.
   */
  mainAgentModel?: string;
  /**
   * Model for source agents (per-source query generation).
   * Format: "provider/model-name" (e.g., "anthropic/claude-haiku-4-5-20251001")
   * If not set, uses the provider's default model.
   */
  sourceAgentModel?: string;
  /**
   * Separate model configuration for DASH_COMP flow (dashboard component picking)
   * If not provided, falls back to provider-based model selection
   */
  dashCompModels?: DashCompModelConfig;
  /**
   * Similarity threshold for conversation search (semantic matching)
   * Value between 0 and 1 (e.g., 0.8 = 80% similarity required)
   * Higher values require closer matches, lower values allow more distant matches
   * Default: 0.8
   */
  conversationSimilarityThreshold?: number;
  /**
   * Query cache TTL (Time To Live) in minutes
   * Cached query results expire after this duration
   * Default: 5 minutes
   */
  queryCacheTTL?: number;
  /**
   * Dashboard conversation history TTL (Time To Live) in minutes
   * Per-dashboard conversation histories expire after this duration
   * Default: 30 minutes
   */
  dashboardHistoryTTL?: number;
}

// ==================== Dashboard CRUD Message Schemas ====================

// Import DSL types from dashboards module
import { DSLRendererPropsSchema } from './dashboards/types';
export type { DSLRendererProps } from './dashboards/types';

// Query filters schema for dashboards
export const DashboardQueryFiltersSchema = z.object({
  dashboardId: z.string().optional(),
  projectId: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
  name: z.string().optional(),
  published: z.boolean().optional(),
});

export type DashboardQueryFilters = z.infer<typeof DashboardQueryFiltersSchema>;

// Dashboard CRUD request payload
export const DashboardsRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    dashboardId: z.string().optional(),
    projectId: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    published: z.boolean().optional(),
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    allowedUsers: z.array(z.string()).optional(),
    dashboard: DSLRendererPropsSchema.optional(),
    // Query operation fields
    filters: DashboardQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
  }).optional(),
});

export type DashboardsRequestPayload = z.infer<typeof DashboardsRequestPayloadSchema>;

export const DashboardsRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('DASHBOARDS'),
  payload: DashboardsRequestPayloadSchema,
});

export type DashboardsRequestMessage = z.infer<typeof DashboardsRequestMessageSchema>;

// ==================== Report CRUD Message Schemas ====================

// Import DSL types from reports module
import { DSLRendererPropsSchema as ReportDSLRendererPropsSchema } from './reports/types';
export type { DSLRendererProps as ReportDSLRendererProps } from './reports/types';

// Query filters schema for reports
export const ReportQueryFiltersSchema = z.object({
  reportId: z.string().optional(),
  projectId: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
  name: z.string().optional(),
  published: z.boolean().optional(),
});

export type ReportQueryFilters = z.infer<typeof ReportQueryFiltersSchema>;

// Report CRUD request payload
export const ReportsRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    reportId: z.string().optional(),
    projectId: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    published: z.boolean().optional(),
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    report: ReportDSLRendererPropsSchema.optional(),
    // Query operation fields
    filters: ReportQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
  }).optional(),
});

export type ReportsRequestPayload = z.infer<typeof ReportsRequestPayloadSchema>;

export const ReportsRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('REPORTS'),
  payload: ReportsRequestPayloadSchema,
});

export type ReportsRequestMessage = z.infer<typeof ReportsRequestMessageSchema>;

// ==================== UIs CRUD Message Schemas ====================

// Import DSL types from uis module (same as dashboards)
import { DSLRendererPropsSchema as UIDSLRendererPropsSchema } from './dashboards/types';
export type { DSLRendererProps as UIDSLRendererProps } from './dashboards/types';

// Query filters schema for UIs
export const UIQueryFiltersSchema = z.object({
  uiId: z.string().optional(),
  projectId: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
  name: z.string().optional(),
});

export type UIQueryFilters = z.infer<typeof UIQueryFiltersSchema>;

// UI CRUD request payload
export const UIsRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    uiId: z.string().optional(),
    projectId: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    ui: UIDSLRendererPropsSchema.optional(),
    // Query operation fields
    filters: UIQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
  }).optional(),
});

export type UIsRequestPayload = z.infer<typeof UIsRequestPayloadSchema>;

export const UIsRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('UIS'),
  payload: UIsRequestPayloadSchema,
});

export type UIsRequestMessage = z.infer<typeof UIsRequestMessageSchema>;

// ==================== UI Block (Conversation History) ====================

/**
 * UIBlock represents a stored conversation response with component and actions
 * Used for semantic search matching in conversation history
 */
export const UIBlockSchema = z.object({
  id: z.string().optional(),
  userQuestion: z.string().optional(),
  user_prompt: z.string().optional(), // DB field name
  text: z.string().optional(),
  textResponse: z.string().optional(),
  analysis: z.string().optional(), // DB field name for textResponse
  component: ComponentSchema.optional(), // DB field name / Legacy field
  generatedComponentMetadata: ComponentSchema.optional(), // Actual field used by UIBlock class
  componentData: z.record(z.any()).optional(),
  actions: z.array(z.any()).optional(),
  isFetchingActions: z.boolean().optional(),
  createdAt: z.string().optional(),
  metadata: z.object({
    timestamp: z.number().optional(),
    userPrompt: z.string().optional(),
    similarity: z.number().optional(),
  }).optional(),
});

export type UIBlock = z.infer<typeof UIBlockSchema>;

// ==================== Bookmarks CRUD Message Schemas ====================

// DBUIBlock schema for database storage (matches DBUIBlock interface from conversation-saver.ts)
export const DBUIBlockSchema = z.object({
  id: z.string(),
  component: z.record(z.string(), z.any()).nullable(),
  analysis: z.string().nullable(),
  user_prompt: z.string(),
});

// Bookmark data schema
export const BookmarkDataSchema = z.object({
  id: z.number().optional(),
  uiblock: DBUIBlockSchema, // Typed JSON object
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type BookmarkData = z.infer<typeof BookmarkDataSchema>;

// Query filters schema for bookmarks
export const BookmarkQueryFiltersSchema = z.object({
  userId: z.string().optional(),
  threadId: z.string().optional(),
  name: z.string().optional(),
});

export type BookmarkQueryFilters = z.infer<typeof BookmarkQueryFiltersSchema>;

// Bookmarks CRUD request payload
export const BookmarksRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    userId: z.string().optional(),
    threadId: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    uiblock: DBUIBlockSchema.optional(),
    // Query operation fields
    filters: BookmarkQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
  }).optional(),
});

export type BookmarksRequestPayload = z.infer<typeof BookmarksRequestPayloadSchema>;

export const BookmarksRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('BOOKMARKS'),
  payload: BookmarksRequestPayloadSchema,
});

export type BookmarksRequestMessage = z.infer<typeof BookmarksRequestMessageSchema>;

// Artifacts request schema
export const ArtifactsQueryFiltersSchema = z.object({
  createdBy: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  name: z.string().optional(),
  deleted: z.boolean().optional(),
  createdAt: z.string().optional(),
});

export type ArtifactsQueryFilters = z.infer<typeof ArtifactsQueryFiltersSchema>;

export const ArtifactsRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'query']),
  data: z.object({
    id: z.number().optional(),
    name: z.string().optional(),
    createdBy: z.string().optional(),
    dsl: z.record(z.any()).optional(),
    status: z.string().optional(),
    deleted: z.boolean().optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
    // Query operation fields
    filters: ArtifactsQueryFiltersSchema.optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
    // Menu grouping fields
    type: z.string().optional(),
    menuId: z.number().optional(),
    artifactGroupName: z.string().optional(),
    artifactGroupId: z.string().optional(),
    artifactGroupIcon: z.string().optional(),
  }).optional(),
});

export type ArtifactsRequestPayload = z.infer<typeof ArtifactsRequestPayloadSchema>;

export const ArtifactsRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('ARTIFACTS'),
  payload: ArtifactsRequestPayloadSchema,
});

export type ArtifactsRequestMessage = z.infer<typeof ArtifactsRequestMessageSchema>;

// KB Nodes (Knowledge Base) request schema
export const KbNodeTypeSchema = z.enum(['global', 'user', 'query']);
export type KbNodeType = z.infer<typeof KbNodeTypeSchema>;

export const KbNodesQueryFiltersSchema = z.object({
  query: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  type: KbNodeTypeSchema.optional(),
  createdBy: z.string().optional(),
});

export type KbNodesQueryFilters = z.infer<typeof KbNodesQueryFiltersSchema>;

export const KbNodesRequestPayloadSchema = z.object({
  operation: z.enum(['create', 'update', 'delete', 'getAll', 'getOne', 'search', 'getByCategory', 'getByUser', 'getCategories', 'getTags']),
  data: z.object({
    id: z.number().optional(),
    title: z.string().optional(),
    content: z.string().optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
    type: KbNodeTypeSchema.optional(),
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    userId: z.string().optional(),
    // Query/search operation fields
    query: z.string().optional(),
    filters: KbNodesQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
  }).optional(),
});

export type KbNodesRequestPayload = z.infer<typeof KbNodesRequestPayloadSchema>;

export const KbNodesRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('KB_NODES'),
  payload: KbNodesRequestPayloadSchema,
});

export type KbNodesRequestMessage = z.infer<typeof KbNodesRequestMessageSchema>;

// ==================== Menus CRUD Message Schemas ====================

// Query filters schema for menus
export const MenusQueryFiltersSchema = z.object({
  parentId: z.number().nullable().optional(),
  isActive: z.boolean().optional(),
});

export type MenusQueryFilters = z.infer<typeof MenusQueryFiltersSchema>;

// Menus CRUD request payload
export const MenusRequestPayloadSchema = z.object({
  operation: z.enum([
    'create',
    'update',
    'delete',
    'getAll',
    'getOne',
    'getRootMenus',
    'getChildMenus',
    'getHierarchy',
    'query',
    'reorder'
  ]),
  data: z.object({
    id: z.number().optional(),
    name: z.string().optional(),
    componentName: z.string().optional(),
    icon: z.string().optional(),
    userMessage: z.string().optional(),
    parentId: z.number().nullable().optional(),
    sortOrder: z.number().optional(),
    props: z.record(z.unknown()).optional(),
    isActive: z.boolean().optional(),
    // Query operation fields
    filters: MenusQueryFiltersSchema.optional(),
    limit: z.number().optional(),
    sort: z.enum(['ASC', 'DESC']).optional(),
    // Reorder operation fields
    items: z.array(z.object({
      id: z.number(),
      sortOrder: z.number(),
    })).optional(),
    menuJson: z.record(z.unknown()).optional(),
  version: z.number().optional(),
  }).optional(),

});

export type MenusRequestPayload = z.infer<typeof MenusRequestPayloadSchema>;

export const MenusRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('MENUS'),
  payload: MenusRequestPayloadSchema,
});

export type MenusRequestMessage = z.infer<typeof MenusRequestMessageSchema>;

// Menus response payload
export const MenusResponsePayloadSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  data: z.any().optional(),
  count: z.number().optional(),
  message: z.string().optional(),
});

export type MenusResponsePayload = z.infer<typeof MenusResponsePayloadSchema>;

// ==================== Dashboard Component Request Schema ====================

export const DashCompRequestPayloadSchema = z.object({
  prompt: z.string(),
  userId: z.string().optional(),
  /** Dashboard ID for scoping conversation history */
  dashboardId: z.string().optional(),
  SA_RUNTIME: z.object({
    threadId: z.string().optional(),
    uiBlockId: z.string().optional(),
  }).optional(),
  /** Existing components in the dashboard (for update/filter operations) */
  existingComponents: z.array(ComponentSchema).optional(),
  /** Request type: create (new component), update (modify existing), filter (create filter + update components) */
  req_type: z.enum(['create', 'update', 'filter']).optional(),
});

export type DashCompRequestPayload = z.infer<typeof DashCompRequestPayloadSchema>;

export const DashCompRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('DASH_COMP_REQ'),
  payload: DashCompRequestPayloadSchema,
});

export type DashCompRequestMessage = z.infer<typeof DashCompRequestMessageSchema>;

// ==================== Report Component Request Schema ====================

export const ReportCompRequestPayloadSchema = z.object({
  prompt: z.string(),
  userId: z.string().optional(),
  reportId: z.string().optional(),
  projectId: z.string().optional(),
  SA_RUNTIME: z.object({
    threadId: z.string().optional(),
    uiBlockId: z.string().optional(),
  }).optional(),
});

export type ReportCompRequestPayload = z.infer<typeof ReportCompRequestPayloadSchema>;

export const ReportCompRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('REPORT_COMP_REQ'),
  payload: ReportCompRequestPayloadSchema,
});

export type ReportCompRequestMessage = z.infer<typeof ReportCompRequestMessageSchema>;

// ==================== Schema Request/Response ====================

/**
 * Schema column statistics
 */
export const SchemaColumnStatisticsSchema = z.object({
  distinct: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});

export type SchemaColumnStatistics = z.infer<typeof SchemaColumnStatisticsSchema>;

/**
 * Schema column definition
 */
export const SchemaColumnSchema = z.object({
  name: z.string(),
  type: z.string(),
  nativeType: z.string(),
  nullable: z.boolean(),
  description: z.string(),
  statistics: SchemaColumnStatisticsSchema.optional(),
  cardinality: z.enum(['unique', 'high', 'medium', 'low']).optional(),
  sampleValues: z.array(z.string()).optional(),
});

export type SchemaColumn = z.infer<typeof SchemaColumnSchema>;

/**
 * Schema table definition
 */
export const SchemaTableSchema = z.object({
  name: z.string(),
  fullName: z.string(),
  rowCount: z.number(),
  description: z.string(),
  columns: z.array(SchemaColumnSchema),
});

export type SchemaTable = z.infer<typeof SchemaTableSchema>;

/**
 * Schema relationship definition
 */
export const SchemaRelationshipSchema = z.object({
  from: z.string(),
  to: z.string(),
  type: z.string(),
  keys: z.array(z.string()),
});

export type SchemaRelationship = z.infer<typeof SchemaRelationshipSchema>;

/**
 * Database schema definition
 */
export const DatabaseSchemaSchema = z.object({
  database: z.string(),
  databaseType: z.string().optional(),
  schema: z.string(),
  description: z.string(),
  tables: z.array(SchemaTableSchema),
  relationships: z.array(SchemaRelationshipSchema).optional(),
});

export type DatabaseSchema = z.infer<typeof DatabaseSchemaSchema>;

/**
 * Schema request payload
 */
export const SchemaRequestPayloadSchema = z.object({
  /** If true, returns the formatted documentation string in addition to raw JSON */
  formatted: z.boolean().optional(),
});

export type SchemaRequestPayload = z.infer<typeof SchemaRequestPayloadSchema>;

/**
 * Schema request message
 */
export const SchemaRequestMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('SCHEMA_REQ'),
  payload: SchemaRequestPayloadSchema,
});

export type SchemaRequestMessage = z.infer<typeof SchemaRequestMessageSchema>;

/**
 * Schema response payload
 */
export const SchemaResponsePayloadSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  data: z.object({
    schema: DatabaseSchemaSchema,
    /** Formatted schema documentation (only if formatted: true was requested) */
    formatted: z.string().optional(),
  }).optional(),
});

export type SchemaResponsePayload = z.infer<typeof SchemaResponsePayloadSchema>;

/**
 * Schema response message
 */
export const SchemaResponseMessageSchema = z.object({
  id: z.string(),
  from: MessageParticipantSchema,
  type: z.literal('SCHEMA_RES'),
  payload: SchemaResponsePayloadSchema,
});

export type SchemaResponseMessage = z.infer<typeof SchemaResponseMessageSchema>;

// ==================== Response Types ====================

// Standard response format for LLM operations
export interface T_RESPONSE {
  success: boolean;
  data?: any;
  errors: string[];
}

export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  addEventListener(event: string, listener: (event: any) => void): void;
  removeEventListener(event: string, listener: (event: any) => void): void;
  readyState: number;
  CONNECTING: number;
  OPEN: number;
  CLOSING: number;
  CLOSED: number;
}

